/**
 * The arena.
 *
 * A Pokémon battle screen, drawn with the art this game already has: the same 16×20 characters
 * baked at 3× and 4×, the same palette, the same hand-written font, the same integer scaling, the
 * same `elapsed`-driven animation idioms as `Engine`. Nothing new loads and nothing is imported.
 *
 * Two slots in the reference layout translate themselves and are worth naming, because they are the
 * reason this furniture fits a product that cut its league:
 *
 *   · **`Lv.42` is the island.** GDD §2.1: „Nivo = ostrvo na kome si. Ti si na ostrvu REZ — to JE
 *     tvoj nivo. Nema paralelne lestvice rangova." The plate says `♦ REZ` and there is no number.
 *   · **The health bar is the deadline, then the vote.** Both are real quantities that already
 *     exist. Neither is a score, and neither survives the duel.
 *
 * Damage numbers are never drawn. The reference does not draw them either — the bar moves and the
 * text box says which move landed, which is all a bar is for.
 *
 * Shaped like `Engine` on purpose: `start()` / `stop()` / `resize()` / `refresh()`, an offscreen
 * buffer blitted whole, and a React screen that constructs it once and pushes fresh state in.
 */

import { OSA_LABELS, copy } from '../../core/copy';
import {
  MIN_GLASOVA,
  clockHealth,
  deadlineNow,
  ishodOf,
  nameOfSide,
  other,
  stageOf,
  superEffective,
  totalVotes,
  voteHealth,
  type Duel,
  type Osa,
  type Side,
  type Tally,
} from '../../core/dvoboj';
import { sfx } from '../audio';
import { CELL_H, LINE_HEIGHT, drawText, measureText, wrapText } from '../art/font';
import { palette } from '../art/palette';
import { decodeLook } from '../../core/look';
import { bake, heroColors, heroPose } from '../art/sprites';
import { panel } from './engine';

/* ------------------------------------------------------------------ what it draws */

export interface Fighter {
  name: string;
  look: number;
  souvenirs: string[];
  /** The island they are standing on — the `Lv.` slot. Null before they have opened one. */
  island: string | null;
}

export interface BitkaState {
  duel: Duel;
  tally: Tally;
  fighters: [Fighter, Fighter];
  /** Which side the viewer is, or null when they are here to vote rather than to fight. */
  me: Side | null;
  brief: string;
  /** Server time, so a device with a wrong clock does not run a different deadline. */
  now: number;
}

/** The four commands, in the FIGHT / BAG / POKéMON / RUN positions and colours. */
export type Command = 'predaj' | 'zadatak' | 'protivnik' | 'izadji';
const COMMANDS: readonly Command[] = ['predaj', 'zadatak', 'protivnik', 'izadji'];
const COMMAND_COLOURS: Record<Command, string> = {
  predaj: '#c8453c',
  zadatak: '#b07a2e',
  protivnik: '#3f9a55',
  izadji: '#2f6fae',
};

export interface BitkaHooks {
  onCommand(command: Command): void;
}

/* ------------------------------------------------------------------ timing */

const INTRO_MS = 900;
const HIT_MS = 620;
const FAINT_MS = 900;
const TYPE_MS_PER_CHAR = 18;
/** How long a fully-typed battle line is held before the next one takes over. */
const HOLD_MS = 1100;
/** Health units per second the bar travels. Slow enough to watch, fast enough not to lie. */
const HP_PER_SEC = 46;
/** Attacks that may be waiting to play. Past this the bar has long since told the same story. */
const MAX_PENDING = 6;

type Anim =
  | { kind: 'ulaz'; start: number }
  | { kind: 'napad'; start: number; by: Side; axis: Osa; strong: boolean }
  | { kind: 'pad'; start: number; who: Side }
  | null;

export class Bitka {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly buffer: HTMLCanvasElement;
  private readonly bufferCtx: CanvasRenderingContext2D;

  private raf = 0;
  private lastTime = 0;
  private elapsed = 0;

  private scale = 3;
  private viewW = 264;
  private viewH = 198;

  private state: BitkaState;
  private anim: Anim = { kind: 'ulaz', start: 0 };
  private pending: Array<{ by: Side; axis: Osa; strong: boolean }> = [];

  /** Where the bars are drawn, easing toward where they should be. */
  private shown: [number, number] = [100, 100];
  private lines: string[] = [];
  private revealed = 0;
  private lineHold = 0;
  private cursor = 0;
  /** Set once per side, so the low-health pulse happens exactly once. */
  private beeped: [boolean, boolean] = [false, false];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    state: BitkaState,
    private readonly hooks: BitkaHooks,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.buffer = document.createElement('canvas');
    const bufferCtx = this.buffer.getContext('2d');
    if (!bufferCtx) throw new Error('no 2d context');
    this.bufferCtx = bufferCtx;
    this.state = state;
    this.shown = [this.targetHealth(0), this.targetHealth(1)];
    this.say(this.openingLines());
    this.resize();
  }

  /* ---------------------------------------------------------------- lifecycle */

  start(): void {
    if (this.raf) return;
    // The sting, on the way in. Entering an arena is a moment, and this is the only place in the
    // product where that is true — everywhere else the app is deliberately quiet.
    if (this.anim?.kind === 'ulaz') sfx.izazov();
    this.lastTime = performance.now();
    const loop = (time: number) => {
      // Clamped exactly as the map's loop is: a tab that was in the background must not resolve a
      // whole battle in one frame when it comes back.
      const delta = Math.min(64, time - this.lastTime);
      this.lastTime = time;
      this.elapsed += delta;
      this.update(delta);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const cssW = Math.max(160, Math.round(rect.width));
    const cssH = Math.max(120, Math.round(rect.height));
    // A target width rather than a tile count: the arena has no tiles. Landing the buffer between
    // roughly 180 and 260 logical pixels keeps a 4× back sprite about a third of the screen wide on
    // a phone and on a desktop alike.
    const target = cssW < 560 ? 200 : 264;
    // Whole-number scaling only, for the same reason as everywhere else: a scale of 2.4 would put
    // half-pixels in the font.
    this.scale = Math.max(1, Math.min(6, Math.round(cssW / target)));
    this.viewW = Math.ceil(cssW / this.scale);
    this.viewH = Math.ceil(cssH / this.scale);

    const dpr = Math.max(1, Math.min(3, Math.round(window.devicePixelRatio || 1)));
    this.canvas.width = this.viewW * this.scale * dpr;
    this.canvas.height = this.viewH * this.scale * dpr;
    this.buffer.width = this.viewW;
    this.buffer.height = this.viewH;
    this.bufferCtx.imageSmoothingEnabled = false;
    this.ctx.imageSmoothingEnabled = false;
    this.rewrap();
    this.draw();
  }

  /**
   * Fresh state, pushed into the running screen rather than remounting it.
   *
   * Votes that landed while nobody was watching are **not** replayed. Opening the screen catches the
   * bars up in one frame; only what arrives while somebody is there gets to be a move. Forty
   * queued attacks would be a cutscene, and the point of the animation is that it is live.
   */
  refresh(next: BitkaState): void {
    const wasStage = stageOf(this.state.duel, this.state.now);
    const wasVotes = totalVotes(this.state.tally);
    this.state = next;
    const stage = stageOf(next.duel, next.now);

    if (stage !== wasStage) {
      this.say(this.openingLines());
      this.shown = [this.targetHealth(0), this.targetHealth(1)];
      this.beeped = [false, false];
      if (stage === 'ishod') this.settleOutcome();
    } else if (totalVotes(next.tally) !== wasVotes && this.pending.length === 0 && !this.anim) {
      /*
       * Catch up without a cutscene — but only when nothing is being played.
       *
       * A vote that lands live queues an attack and then triggers a refetch, and this branch used
       * to fire on that refetch and snap the bar to its final value before the lunge had even
       * started. The eased travel the whole animation exists for never ran once on the live path.
       */
      this.shown = [this.targetHealth(0), this.targetHealth(1)];
    }
  }

  /** A vote landing right now, relayed over the arena socket. This one gets to be a move. */
  hit(by: Side, axis: Osa): void {
    // Bounded. Each one takes `HIT_MS` to play, so an unbounded queue is a cutscene somebody else
    // can make as long as they like — and the server now rate-limits the route that feeds it.
    if (this.pending.length >= MAX_PENDING) return;
    const strong = superEffective(this.state.tally, other(by), axis);
    this.pending.push({ by, axis, strong });
  }

  /** Somebody arrived in the arena. An event, said once — never a light that stays on. */
  announce(text: string): void {
    this.say([text, ...this.commandLines()]);
  }

  /* ---------------------------------------------------------------- input */

  /** Screen coordinates to a command, for a tap. Returns null outside the menu. */
  pointAt(clientX: number, clientY: number): Command | null {
    if (!this.commandsLive()) return null;
    const rect = this.canvas.getBoundingClientRect();
    const x = (clientX - rect.left) / (rect.width / this.viewW);
    const y = (clientY - rect.top) / (rect.height / this.viewH);
    const menu = this.layout().menu;
    if (x < menu.x || x > menu.x + menu.w || y < menu.y || y > menu.y + menu.h) return null;
    const col = x < menu.x + menu.w / 2 ? 0 : 1;
    const row = y < menu.y + menu.h / 2 ? 0 : 1;
    const index = row * 2 + col;
    this.cursor = index;
    const command = COMMANDS[index];
    if (command) this.hooks.onCommand(command);
    return command ?? null;
  }

  /** Arrow keys move the cursor, Enter picks — the way the reference has always worked. */
  key(name: string): boolean {
    if (!this.commandsLive()) return false;
    if (name === 'ArrowLeft' || name === 'ArrowRight') {
      this.cursor ^= 1;
      sfx.move();
      return true;
    }
    if (name === 'ArrowUp' || name === 'ArrowDown') {
      this.cursor ^= 2;
      sfx.move();
      return true;
    }
    if (name === 'Enter' || name === ' ') {
      const command = COMMANDS[this.cursor];
      if (command) this.hooks.onCommand(command);
      return true;
    }
    return false;
  }

  /** True while the four commands are on screen and usable. */
  commandsLive(): boolean {
    return this.state.me !== null && stageOf(this.state.duel, this.state.now) === 'rad';
  }

  /* ---------------------------------------------------------------- the frame */

  private update(delta: number): void {
    if (this.anim && this.elapsed - this.anim.start > this.animLength(this.anim)) this.anim = null;
    if (!this.anim && this.pending.length > 0) {
      const next = this.pending.shift()!;
      this.anim = { kind: 'napad', start: this.elapsed, by: next.by, axis: next.axis, strong: next.strong };
      if (next.strong) sfx.udarJak();
      else sfx.udar();
      this.say([
        copy.dvoboj.uses(nameOfSide(this.state.duel, next.by), OSA_LABELS[next.axis]),
        ...(next.strong ? [copy.dvoboj.superEffective] : []),
        ...this.commandLines(),
      ]);
    }

    // The bar travels rather than jumping, and it is the only thing on screen that eases.
    const step = (HP_PER_SEC * delta) / 1000;
    for (const side of [0, 1] as const) {
      const target = this.targetHealth(side);
      const gap = target - this.shown[side];
      this.shown[side] = Math.abs(gap) <= step ? target : this.shown[side] + Math.sign(gap) * step;
      if (this.shown[side] <= 22 && !this.beeped[side]) {
        this.beeped[side] = true;
        sfx.malo();
      }
      if (this.shown[side] > 30) this.beeped[side] = false;
    }

    // The typewriter, and the hold that lets a finished line be read before the next one.
    const total = this.lines.join(' ').length;
    if (this.revealed < total) {
      this.revealed = Math.min(total, this.revealed + delta / TYPE_MS_PER_CHAR);
      this.lineHold = 0;
    } else {
      this.lineHold += delta;
    }
  }

  private animLength(anim: NonNullable<Anim>): number {
    return anim.kind === 'ulaz' ? INTRO_MS : anim.kind === 'napad' ? HIT_MS : FAINT_MS;
  }

  private targetHealth(side: Side): number {
    const { duel, tally, now } = this.state;
    const stage = stageOf(duel, now);
    if (stage === 'glasanje') return voteHealth(tally, side, false);
    if (stage === 'ishod') {
      const ishod = ishodOf(duel, tally, now);
      if (ishod.kind === 'predaja') return ishod.winner === side ? 100 : 0;
      if (ishod.kind === 'pobeda') return voteHealth(tally, side, true);
      return voteHealth(tally, side, false);
    }
    return clockHealth(duel, side, now);
  }

  private settleOutcome(): void {
    const { duel, tally, now, me } = this.state;
    const ishod = ishodOf(duel, tally, now);
    if (ishod.kind === 'pobeda' || ishod.kind === 'predaja') {
      const loser = other(ishod.winner);
      this.anim = { kind: 'pad', start: this.elapsed, who: loser };
      if (me === null) return;
      if (me === ishod.winner) sfx.pobeda();
      else sfx.poraz();
    }
  }

  /* ---------------------------------------------------------------- the words */

  private say(lines: string[]): void {
    this.lines = lines.filter(Boolean);
    this.revealed = 0;
    this.lineHold = 0;
    this.rewrap();
  }

  private commandLines(): string[] {
    return this.commandsLive() ? [copy.dvoboj.yourTurn] : [];
  }

  private openingLines(): string[] {
    const { duel, tally, now, me } = this.state;
    const stage = stageOf(duel, now);
    const them = me === null ? null : nameOfSide(duel, other(me));
    switch (stage) {
      case 'poziv':
      case 'izmena':
        return them ? [copy.dvoboj.incomingFrom(them)] : [copy.dvoboj.title];
      case 'rad': {
        const mine = me !== null && (me === 0 ? duel.fromAt : duel.toAt) !== null;
        return [
          copy.dvoboj.briefIs(this.state.brief),
          ...(mine ? [copy.dvoboj.waitingForTheirs] : []),
          ...this.commandLines(),
        ];
      }
      case 'glasanje':
        return [copy.dvoboj.bothIn, copy.dvoboj.voting];
      case 'ishod':
        return this.outcomeLines();
      default:
        return [copy.dvoboj.forfeited];
    }
  }

  private outcomeLines(): string[] {
    const { duel, tally, now, me } = this.state;
    const ishod = ishodOf(duel, tally, now);
    if (ishod.kind === 'nista') return [copy.dvoboj.nothing];
    if (ishod.kind === 'nereseno')
      return [ishod.reason === 'malo-glasova' ? copy.dvoboj.drawFew : copy.dvoboj.drawTied];
    if (ishod.kind === 'predaja')
      return [copy.dvoboj.walkover, copy.dvoboj.wonBy(nameOfSide(duel, ishod.winner))];
    const decided = ishod.decisive ? copy.dvoboj.decided[ishod.decisive] : null;
    if (me === null)
      return [copy.dvoboj.wonBy(nameOfSide(duel, ishod.winner)), ...(decided ? [decided] : [])];
    return [me === ishod.winner ? copy.dvoboj.won : copy.dvoboj.lost, ...(decided ? [decided] : [])];
  }

  private rewrap(): void {
    // Nothing cached: the wrap is recomputed in `drawDialogue` from the same width the box uses,
    // so a resize mid-sentence cannot leave the reveal counting characters from a stale layout.
    this.revealed = Math.min(this.revealed, this.lines.join(' ').length);
  }

  /* ---------------------------------------------------------------- layout */

  private layout() {
    const w = this.viewW;
    const h = this.viewH;
    const barH = Math.max(42, Math.min(56, Math.round(h * 0.26)));
    const barY = h - barH;
    const plateW = Math.min(118, Math.round(w * 0.46));
    const menuW = Math.min(134, Math.round(w * 0.46));

    /*
     * The sprites are scaled to the room there actually is. A phone in portrait gives a buffer
     * barely a hundred pixels tall, and a 4× back sprite in it would stand with its head above the
     * sky — the one failure mode a fixed scale guarantees on somebody else's screen.
     */
    const myScale = barY >= 150 ? 4 : barY >= 92 ? 3 : 2;
    const foeScale = Math.max(2, myScale - 1);

    return {
      w,
      h,
      barY,
      myScale,
      foeScale,
      horizon: Math.round(barY * 0.40),
      /** The opponent: up and to the right, small, facing you. */
      foe: { x: Math.round(w * 0.70), y: Math.round(barY * 0.46) },
      foePlate: { x: 4, y: 5, w: plateW, h: 27 },
      /** You: down and to the left, larger, seen from behind. */
      mine: { x: Math.round(w * 0.28), y: barY - 4 },
      myPlate: { x: w - plateW - 4, y: Math.max(barY - 44, Math.round(barY * 0.60)), w: plateW, h: 38 },
      /*
       * The text box takes the whole width whenever there is no menu beside it. The reference does
       * the same thing between turns, and the alternative here is an empty white rectangle where
       * four buttons used to be.
       */
      dialog: {
        x: 3,
        y: barY + 2,
        w: this.commandsLive() ? w - menuW - 9 : w - 6,
        h: barH - 5,
      },
      menu: { x: w - menuW - 3, y: barY + 2, w: menuW, h: barH - 5 },
    };
  }

  /* ---------------------------------------------------------------- drawing */

  private draw(): void {
    const ctx = this.bufferCtx;
    const L = this.layout();
    this.drawBackdrop(ctx, L);

    const intro = this.anim?.kind === 'ulaz' ? Math.min(1, (this.elapsed - this.anim.start) / INTRO_MS) : 1;

    this.drawPlatform(ctx, L.foe.x, L.foe.y, 26, 7);
    this.drawPlatform(ctx, L.mine.x, L.mine.y, 34, 9);

    // Sliding in from opposite edges, which is the one thing the reference does that a still frame
    // cannot: the two of them arrive rather than being found already standing there.
    const slideFoe = Math.round((1 - ease(intro)) * (this.viewW - L.foe.x + 40));
    const slideMine = Math.round((1 - ease(intro)) * -(L.mine.x + 50));

    // Back to front: the far sandbar, then the near one.
    this.drawFighter(ctx, this.foeSide(), L, slideFoe, slideMine);
    this.drawFighter(ctx, this.mySide(), L, slideFoe, slideMine);

    if (intro >= 1) {
      this.drawPlate(ctx, this.foeSide(), L.foePlate, false);
      this.drawPlate(ctx, this.mySide(), L.myPlate, true);
    }

    this.drawDialogue(ctx, L);
    if (this.commandsLive()) this.drawMenu(ctx, L);

    // A single flash frame at the very start. Cheap, and it is what makes the arena feel entered
    // rather than navigated to.
    if (intro < 0.22) {
      ctx.fillStyle = `rgba(255,255,255,${(0.22 - intro) * 4})`;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
    }

    this.ctx.imageSmoothingEnabled = false;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(
      this.buffer,
      0,
      0,
      this.viewW,
      this.viewH,
      0,
      0,
      this.canvas.width,
      this.canvas.height,
    );
  }

  /** Whoever lost, once the duel is over. Null while it is still running, or on a draw. */
  private loser(): Side | null {
    if (stageOf(this.state.duel, this.state.now) !== 'ishod') return null;
    const ishod = ishodOf(this.state.duel, this.state.tally, this.state.now);
    return ishod.kind === 'pobeda' || ishod.kind === 'predaja' ? other(ishod.winner) : null;
  }

  /** Which side is drawn at the back — the opponent, or side 1 when a spectator is watching. */
  private foeSide(): Side {
    return this.state.me === null ? 1 : other(this.state.me);
  }

  private mySide(): Side {
    return this.state.me ?? 0;
  }

  /**
   * The shallows, at the hour the sea goes gold.
   *
   * Two sandbars in shallow water: the same palette the archipelago is drawn from, so the arena
   * reads as a place in this world rather than as a screen bolted onto it.
   */
  private drawBackdrop(ctx: CanvasRenderingContext2D, L: ReturnType<Bitka['layout']>): void {
    // Sky: three bands, and a two-pixel warm line right on the waterline. Any wider and the glow
    // stops being the sun on the horizon and starts being a beach in the middle of the sea.
    const sky: Array<[string, number]> = [
      ['#2f7fb8', 0],
      ['#5cb4dd', Math.round(L.horizon * 0.42)],
      ['#9fe0f0', Math.round(L.horizon * 0.74)],
    ];
    for (let i = 0; i < sky.length; i += 1) {
      const [colour, top] = sky[i]!;
      const bottom = i + 1 < sky.length ? sky[i + 1]![1] : L.horizon;
      ctx.fillStyle = colour;
      ctx.fillRect(0, top, this.viewW, bottom - top);
    }
    ctx.fillStyle = '#f4e3b4';
    ctx.fillRect(0, L.horizon - 2, this.viewW, 2);

    const depth = L.barY - L.horizon;
    const sea: Array<[string, number]> = [
      [palette.seaDeep, L.horizon],
      [palette.sea, L.horizon + Math.round(depth * 0.16)],
      [palette.seaLight, L.horizon + Math.round(depth * 0.40)],
      [palette.shallow, L.horizon + Math.round(depth * 0.70)],
    ];
    for (let i = 0; i < sea.length; i += 1) {
      const [colour, top] = sea[i]!;
      const bottom = i + 1 < sea.length ? sea[i + 1]![1] : L.barY;
      ctx.fillStyle = colour;
      ctx.fillRect(0, top, this.viewW, bottom - top);
      // A line of foam where each depth ends, the same trick `applyShoreline` uses on the map. It
      // is what stops four flat rectangles from reading as four flat rectangles.
      if (i > 0) {
        ctx.fillStyle = 'rgba(205,241,250,0.30)';
        ctx.fillRect(0, top, this.viewW, 1);
      }
    }

    // The horizon, one pixel of foam. Without it the sky and the sea are one flat field.
    ctx.fillStyle = palette.foam;
    ctx.fillRect(0, L.horizon, this.viewW, 1);

    // Sparkles, on the same modulo-frame idiom the map's water uses. Deterministic per column, so
    // they twinkle rather than crawl.
    const frame = Math.floor(this.elapsed / 420) % 4;
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    for (let x = 3; x < this.viewW; x += 7) {
      const h = (Math.imul(x + 1, 2654435761) >>> 24) % 8;
      if ((h + frame) % 4 !== 0) continue;
      const y = L.horizon + 4 + ((h * 13) % Math.max(1, L.barY - L.horizon - 8));
      ctx.fillRect(x, y, 2, 1);
    }
  }

  /** A sandbar: wet rim, dry top, one line of foam where it meets the water. */
  private drawPlatform(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number): void {
    fillEllipse(ctx, cx, cy, rx, ry, palette.sandWet);
    fillEllipse(ctx, cx, cy - 1, rx - 2, ry - 1, palette.sandDark);
    fillEllipse(ctx, cx, cy - 2, rx - 5, ry - 2, palette.sand);
    ctx.fillStyle = palette.foam;
    ctx.fillRect(cx - rx, cy + ry, rx * 2 + 1, 1);
  }

  private drawFighter(
    ctx: CanvasRenderingContext2D,
    side: Side,
    L: ReturnType<Bitka['layout']>,
    slideFoe: number,
    slideMine: number,
  ): void {
    const back = side === this.mySide();
    const fighter = this.state.fighters[side];
    const look = decodeLook(fighter.look);
    const scale = back ? L.myScale : L.foeScale;
    // The back view is the same fourteen body rows every character in this game is made of. That is
    // the whole reason a battle sprite needed no new art: `BODY_UP` already exists because people
    // walk away from the camera on the map.
    const { key, rows, flip } = heroPose(back ? 'up' : 'down', 'stand', look, fighter.souvenirs);
    const sprite = bake(`bitka:${fighter.look}:${key}:${scale}`, rows, heroColors(look), scale, flip);

    const spot = back ? L.mine : L.foe;
    let x = spot.x - sprite.width / 2 + (back ? slideMine : slideFoe);
    // Feet on the sandbar, and never with the head above the sky whatever the window is doing.
    let y = Math.max(2, spot.y - sprite.height + (back ? 4 : 2));

    // A one-pixel idle, out of phase between the two so they do not breathe in lockstep.
    y += Math.sin((this.elapsed + side * 640) / 1000) > 0.62 ? 1 : 0;

    let flash = false;
    if (this.anim?.kind === 'napad') {
      const t = (this.elapsed - this.anim.start) / HIT_MS;
      if (this.anim.by === side) {
        // Out and back: the lunge. A triangle rather than an ease, so it hits and returns.
        const lunge = Math.round(Math.sin(Math.min(1, t * 2.4) * Math.PI) * (back ? 7 : 5));
        y += back ? -lunge : lunge;
        x += back ? lunge : -lunge;
      } else if (t > 0.34 && t < 0.72) {
        flash = Math.floor((this.elapsed - this.anim.start) / 70) % 2 === 0;
      }
    }
    // The winner's hop, while the other one is sitting down. Two frames of air, and then it is
    // over — a victory dance in a product about somebody else's work would be the wrong feature.
    if (this.anim?.kind === 'pad' && this.anim.who !== side) {
      const t = Math.min(1, (this.elapsed - this.anim.start) / FAINT_MS);
      y -= Math.round(Math.abs(Math.sin(t * Math.PI * 2)) * 3);
    }
    if (this.anim?.kind === 'pad' && this.anim.who === side) {
      const t = Math.min(1, (this.elapsed - this.anim.start) / FAINT_MS);
      // Not a faint. They sit down. Nothing in this product is allowed to look like damage.
      y += Math.round(ease(t) * (sprite.height * 0.35));
      ctx.globalAlpha = 1 - t * 0.35;
    } else if (this.loser() === side) {
      // Derived rather than remembered: somebody opening a duel that was decided yesterday has to
      // find the same picture as the person who watched it happen.
      y += Math.round(sprite.height * 0.35);
      ctx.globalAlpha = 0.65;
    }

    ctx.fillStyle = 'rgba(8,26,44,0.22)';
    ctx.fillRect(Math.round(x + sprite.width * 0.2), spot.y - 1, Math.round(sprite.width * 0.6), 1);

    ctx.drawImage(sprite.canvas, Math.round(x), Math.round(y));
    if (flash) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fillRect(Math.round(x), Math.round(y), sprite.width, sprite.height);
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The health plate.
   *
   * `Lv.42` is the island and there is no number in that slot. The count under the bar — the
   * reference's `83/83` — is drawn only on the viewer's own plate, exactly as it is drawn only for
   * your own Pokémon: the other side's remaining time is not your business, and their vote count
   * next to their name for two days would be a scoreboard with a face on it.
   */
  private drawPlate(
    ctx: CanvasRenderingContext2D,
    side: Side,
    box: { x: number; y: number; w: number; h: number },
    own: boolean,
  ): void {
    const fighter = this.state.fighters[side];
    panel(ctx, box.x, box.y, box.w, box.h, palette.paper);

    drawText(ctx, fighter.name.slice(0, 12).toUpperCase(), box.x + 5, box.y + 4, {
      color: palette.ink,
    });
    if (fighter.island) {
      const label = `♦ ${fighter.island}`;
      const width = measureText(label, {});
      drawText(ctx, label, box.x + box.w - width - 5, box.y + 4, { color: palette.inkSoft });
    }

    const barY = box.y + 4 + CELL_H + 2;
    const stage = stageOf(this.state.duel, this.state.now);
    const label = stage === 'glasanje' || stage === 'ishod' ? 'GLAS' : 'ROK';
    drawText(ctx, label, box.x + 5, barY, { color: palette.goldDark });

    const barX = box.x + 5 + measureText(label, {}) + 3;
    const barW = box.w - (barX - box.x) - 5;
    const value = Math.max(0, Math.min(100, this.shown[side]));
    ctx.fillStyle = palette.ink;
    ctx.fillRect(barX - 1, barY + 1, barW + 2, 6);
    ctx.fillStyle = palette.inkSoft;
    ctx.fillRect(barX, barY + 2, barW, 4);
    ctx.fillStyle =
      value > 50 ? palette.green : value > 20 ? palette.gold : palette.warn;
    ctx.fillRect(barX, barY + 2, Math.round((barW * value) / 100), 4);

    if (!own) return;
    const readout = this.readout(side);
    if (!readout) return;
    const width = measureText(readout, { mono: true });
    drawText(ctx, readout, box.x + box.w - width - 5, barY + CELL_H, {
      color: palette.inkSoft,
      mono: true,
    });
  }

  /** What the reference draws as `83/83`: the time left, then the votes cast. */
  private readout(side: Side): string | null {
    const { duel, tally, now } = this.state;
    const stage = stageOf(duel, now);
    if (stage === 'glasanje' || stage === 'ishod') {
      const total = totalVotes(tally);
      return total < MIN_GLASOVA ? `${total}` : `${tally.votes[side]}`;
    }
    const deadline = deadlineNow(duel, now);
    return deadline === null ? null : shortLeft(deadline - now);
  }

  private drawDialogue(ctx: CanvasRenderingContext2D, L: ReturnType<Bitka['layout']>): void {
    const box = L.dialog;
    panel(ctx, box.x, box.y, box.w, box.h, palette.night);
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    ctx.fillRect(box.x + 2, box.y + 2, box.w - 4, 1);

    const maxWidth = box.w - 10;
    const rows: string[] = [];
    for (const line of this.lines) rows.push(...wrapText(line, maxWidth, {}));

    // One reveal budget across every wrapped row, so the crawl reads as one continuous sentence
    // rather than restarting on each line. Straight out of the map's dialogue box.
    let budget = Math.floor(this.revealed);
    const fit = Math.max(1, Math.floor((box.h - 8) / LINE_HEIGHT));
    const visible = rows.slice(0, fit);
    for (let i = 0; i < visible.length; i += 1) {
      const row = visible[i]!;
      const take = Math.max(0, Math.min(row.length, budget));
      budget -= row.length;
      if (take === 0) break;
      drawText(ctx, row.slice(0, take), box.x + 5, box.y + 5 + i * LINE_HEIGHT, {
        color: palette.paper,
      });
    }

    // The blinking cue, once the line is finished and there is more behind it.
    if (rows.length > fit && this.lineHold > HOLD_MS && Math.floor(this.elapsed / 380) % 2 === 0) {
      drawText(ctx, '▾', box.x + box.w - 10, box.y + box.h - 13, { color: palette.gold });
    }
  }

  private drawMenu(ctx: CanvasRenderingContext2D, L: ReturnType<Bitka['layout']>): void {
    const box = L.menu;
    panel(ctx, box.x, box.y, box.w, box.h, palette.paper);
    const cw = Math.floor((box.w - 6) / 2);
    const ch = Math.floor((box.h - 6) / 2);
    const labels: Record<Command, string> = {
      predaj: copy.dvoboj.menuSubmit,
      zadatak: copy.dvoboj.menuBrief,
      protivnik: copy.dvoboj.menuOpponent,
      izadji: copy.dvoboj.menuLeave,
    };

    for (let i = 0; i < COMMANDS.length; i += 1) {
      const command = COMMANDS[i]!;
      const x = box.x + 3 + (i % 2) * cw;
      const y = box.y + 3 + Math.floor(i / 2) * ch;
      ctx.fillStyle = COMMAND_COLOURS[command];
      ctx.fillRect(x, y, cw - 1, ch - 1);
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      ctx.fillRect(x, y, cw - 1, 1);

      const label = labels[command];
      const chosen = this.cursor === i;
      // The cursor sits inside the button rather than beside it: there is no room for a gutter at
      // this size, and a filled arrow on the label reads at one pixel per pixel.
      const textX = x + 7;
      const room = cw - 10;
      // Tighten rather than clip. „PROTIVNIK" is nine glyphs and it is the word — a label that runs
      // into the next button is worse than one drawn a pixel closer together.
      const style = measureText(label, {}) > room ? { tracking: -1 } : {};
      if (chosen && Math.floor(this.elapsed / 420) % 2 === 0)
        drawText(ctx, '▸', x + 2, y + Math.round((ch - CELL_H) / 2), { color: palette.white });
      drawText(ctx, label, textX, y + Math.round((ch - CELL_H) / 2), {
        ...style,
        color: palette.white,
        shadow: 'rgba(0,0,0,0.45)',
      });
    }
  }
}

/* ------------------------------------------------------------------ helpers */

/** Ease-out. The only easing in this file, and only for things that arrive rather than move. */
const ease = (t: number): number => 1 - (1 - t) * (1 - t);

/** A filled ellipse, painted as horizontal runs. No arcs — an arc would antialias. */
function fillEllipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  fill: string,
): void {
  if (rx <= 0 || ry <= 0) return;
  ctx.fillStyle = fill;
  for (let dy = -ry; dy <= ry; dy += 1) {
    const t = 1 - (dy * dy) / (ry * ry);
    if (t < 0) continue;
    const half = Math.round(rx * Math.sqrt(t));
    ctx.fillRect(cx - half, cy + dy, half * 2 + 1, 1);
  }
}

/**
 * Time left, in the four characters the reference gives `83/83`.
 *
 * Days and hours while there is a day left, then hours and minutes, then minutes and seconds in the
 * last hour — which is the only point at which seconds are information rather than pressure.
 */
export function shortLeft(ms: number): string {
  if (ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

